import React, { useEffect, useMemo, useState } from 'react';

function InlineSparkline({ data = [], width = 480, height = 120, stroke = '#34d399' }) {
  const path = useMemo(() => {
    if (!data.length) return '';
    const max = Math.max(...data);
    const min = Math.min(...data);
    const dx = width / (data.length - 1 || 1);
    const scaleY = (v) => {
      if (max === min) return height / 2;
      return height - ((v - min) / (max - min)) * (height - 12) - 6;
    };
    return data.map((v, i) => `${i === 0 ? 'M' : 'L'} ${i * dx} ${scaleY(v)}`).join(' ');
  }, [data, width, height]);

  const area = useMemo(() => {
    if (!data.length) return '';
    const max = Math.max(...data);
    const min = Math.min(...data);
    const dx = width / (data.length - 1 || 1);
    const scaleY = (v) => {
      if (max === min) return height / 2;
      return height - ((v - min) / (max - min)) * (height - 12) - 6;
    };
    const top = data.map((v, i) => `${i === 0 ? 'M' : 'L'} ${i * dx} ${scaleY(v)}`).join(' ');
    const bottom = `L ${width} ${height} L 0 ${height} Z`;
    return top + ' ' + bottom;
  }, [data, width, height]);

  return (
    <svg width={width} height={height} role="img" aria-label="30 day player peaks chart">
      <defs>
        <linearGradient id="grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#34d399" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#34d399" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill="url(#grad)" stroke="none" />
      <path d={path} fill="none" stroke={stroke} strokeWidth="2" />
    </svg>
  );
}

export default function ServerDetails({ ip, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!ip) return;
    const controller = new AbortController();
    const run = async () => {
      setLoading(true);
      setError(null);
      setData(null);
      try {
        const res = await fetch(`/servers/${ip}`, { signal: controller.signal });
        if (!res.ok) throw new Error('Bad response: ' + res.status);
        const json = await res.json();
        setData(json);
      } catch (e) {
        if (controller.signal.aborted) return;
        try {
          // try ip specific mock
          const r2 = await fetch(`/mocks/servers/${ip}.json`, { signal: controller.signal });
          if (!r2.ok) throw new Error('No ip specific mock');
          const j2 = await r2.json();
          setData(j2);
        } catch (e2) {
          if (controller.signal.aborted) return;
          try {
            const r3 = await fetch(`/mocks/servers/sample.json`, { signal: controller.signal });
            const j3 = await r3.json();
            setData(j3);
          } catch (e3) {
            setError(e3.message || 'Failed to load details');
          }
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    run();
    return () => controller.abort();
  }, [ip]);

  if (!ip) {
    return (
      <div className="container">
        <div className="grid-state">No server selected.</div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="filters-panel" style={{ marginBottom: 16 }}>
        <div className="filters-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <i className="fa-solid fa-circle-info" aria-hidden="true" />
            <span>Server Details</span>
            <span style={{ color: '#9da7b3' }}>|</span>
            <span style={{ color: '#9da7b3' }}>{ip}</span>
          </div>
          <div>
            <button className="btn-plain" onClick={onBack}>
              ← Back
            </button>
          </div>
        </div>
      </div>

      <div className="details" style={{ padding: 16 }}>
        {loading && <div className="grid-state muted">Loading details…</div>}
        {!loading && error && <div className="grid-state error">{String(error)}</div>}
        {!loading && !error && data && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <section className="panel" style={{ padding: 16 }}>
              <h2 style={{ marginTop: 0, marginBottom: 12 }}>Overview</h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', rowGap: 8, columnGap: 12, alignItems: 'center' }}>
                <div style={{ color: '#9da7b3' }}>Players</div>
                <div>{(data.players && (data.players.cur + '/' + data.players.max)) || data.players || '0/0'}</div>
                <div style={{ color: '#9da7b3' }}>Map</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <img src={data.map?.image || '/logo192.png'} alt={data.map?.name || 'Map image'} style={{ width: 80, height: 50, objectFit: 'cover', borderRadius: 8, border: '1px solid rgba(255,255,255,0.08)' }} />
                  <div>
                    <div style={{ fontWeight: 600 }}>{data.map?.name || 'Unknown'}</div>
                    {data.map?.author && <div style={{ color: '#9da7b3', fontSize: 12 }}>by {data.map.author}</div>}
                  </div>
                </div>
              </div>
            </section>
            <section className="panel" style={{ padding: 16 }}>
              <h2 style={{ marginTop: 0, marginBottom: 12 }}>30-day player peaks</h2>
              <InlineSparkline data={data.peaks30d || data.history || []} width={520} height={140} />
            </section>
            <section className="panel" style={{ gridColumn: '1 / span 2', padding: 16 }}>
              <h2 style={{ marginTop: 0, marginBottom: 12 }}>Players</h2>
              {Array.isArray(data.playersList) && data.playersList.length > 0 ? (
                <ul style={{ listStyle: 'none', margin: 0, padding: 0, columns: 2, columnGap: 24 }}>
                  {data.playersList.map((p, idx) => (
                    <li key={idx} style={{ breakInside: 'avoid', padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>{p}</li>
                  ))}
                </ul>
              ) : (
                <div className="grid-state muted">No players listed.</div>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
